import { useState } from 'react';

import { Cell } from '@common/ui/Cell';
import { Separator } from '@common/ui/Separator';
import { Toogle } from '@common/ui/Toogle';
import { useEventEmit } from '@common/utils/events';
import { getApiErrorInfo } from '@common/utils/fetcher';

import {
  disableFeatureOnError,
  isAccount,
  useAccountStore,
} from '@entities/account';

import {
  ACCOUNT_ERROR_CODES,
  ACCOUNT_SETTINGS_REFUSALS,
  type PatchMeSettingsParams,
} from '@services/AccountService';
import { usePatchMeSettings } from '@services/hooks/AccountService';

export type AccountToggleField =
  | 'dating'
  | 'matchmaker'
  | 'hidden'
  | 'allow_recommend';

export type AccountSettingToggleProps = {
  testId: string;
  field: AccountToggleField;
  title: string;
  hint: string;
  hasSeparator?: boolean;
  /** Disabled from outside, e.g. while the other mode row is saving. */
  isLocked?: boolean;
  /** Told when this row's request starts and ends. */
  onPendingChange?: (isPending: boolean) => void;
  /** Runs before a change is sent; `submit` sends it (a confirm can wait). */
  onBeforeChange?: (isChecked: boolean, submit: () => void) => void;
  onProfileIncomplete?: () => void;
};

const isOneModeRequired = (error: unknown) => {
  const { code, details } = getApiErrorInfo(error);

  return (
    code === ACCOUNT_ERROR_CODES.invalidSettings &&
    details?.reason === ACCOUNT_SETTINGS_REFUSALS.oneModeRequired
  );
};

/**
 * One «Анкета» setting. Each row owns its PATCH (an SWR mutation per row:
 * one shared mutation would swallow the error of a request a later row
 * overtook), is disabled while it is in flight, shows the value it asked for
 * meanwhile, then the server's answer, and falls back to the stored value on
 * an error. Only the row's own field is taken from the answer, so answers
 * of two rows that arrive out of order cannot undo each other.
 */
export const AccountSettingToggle = ({
  testId,
  field,
  title,
  hint,
  hasSeparator = false,
  isLocked = false,
  onPendingChange,
  onBeforeChange,
  onProfileIncomplete,
}: AccountSettingToggleProps) => {
  const emit = useEventEmit();
  const storedValue = useAccountStore((state) => state.account?.[field]);
  const { trigger: triggerPatchMeSettings } = usePatchMeSettings();
  const [requestedValue, setRequestedValue] = useState<boolean | null>(null);

  const isPending = requestedValue !== null;

  const showError = (error: unknown) => {
    if (isOneModeRequired(error)) {
      emit('error', { title: 'Оставьте включённым хотя бы один режим' });
      return;
    }

    if (getApiErrorInfo(error).code === ACCOUNT_ERROR_CODES.profileIncomplete) {
      onProfileIncomplete?.();
      return;
    }

    if (disableFeatureOnError(error, 'matchmaker')) {
      emit('info', { title: 'Режим свахи сейчас недоступен' });
      return;
    }

    emit('error', {
      title: 'Не удалось сохранить',
      description: 'Попробуйте ещё раз',
    });
  };

  const submit = async (isChecked: boolean) => {
    const patch: PatchMeSettingsParams = { [field]: isChecked };

    setRequestedValue(isChecked);
    onPendingChange?.(true);

    try {
      const settings: unknown = await triggerPatchMeSettings(patch);

      // A malformed answer is a failed save: the stored value stays.
      if (!isAccount(settings)) {
        throw new Error('PATCH /me/settings answered without settings');
      }

      const { account, setAccount } = useAccountStore.getState();

      setAccount({
        ...(account ?? settings),
        [field]: settings[field],
        features: settings.features,
      });
    } catch (error) {
      showError(error);
    } finally {
      setRequestedValue(null);
      onPendingChange?.(false);
    }
  };

  const isDisabled = isPending || isLocked;

  const handleChange = (isChecked: boolean) => {
    if (isDisabled) return;

    if (onBeforeChange) {
      onBeforeChange(isChecked, () => void submit(isChecked));
      return;
    }

    void submit(isChecked);
  };

  return (
    <Cell
      testId={testId}
      title={title}
      subtitle={hint}
      separator={hasSeparator ? <Separator /> : undefined}
      suffixIcon={
        <Toogle
          testId={`${testId}-toogle`}
          checked={requestedValue ?? storedValue ?? false}
          disabled={isDisabled}
          onChange={handleChange}
        />
      }
    />
  );
};
